import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle, AlertTriangle, Clock, Zap, CreditCard, History, RefreshCw, Users, FolderOpen, Calendar, HardDrive } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { PLAN_PRICING, LIFETIME_PLAN, SALES_EMAIL, fmtBytes, limitLabel } from "@/lib/plans";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = {
  starter: ["Project management & 60+ template tasks", "DPR / bankable reports", "Document vault", "GST-ready POs & PIs", "Email support"],
  growth: ["Everything in Starter", "CRM & proposal generator", "Milestone billing & receivables", "Client portal", "Priority support"],
  enterprise: ["Everything in Growth", "Unlimited projects & users", "100 GB storage", "Dedicated account manager", "White-label reports"],
  lifetime: ["Everything in Scale", "Lifetime access — no renewals", "All future features", "Priority onboarding", "Dedicated support"],
};

// Same numbers as the pricing page (src/lib/plans.js); the server's /billing/plans is preferred when it loads.
const buildPlans = (server) => [...PLAN_PRICING, LIFETIME_PLAN].map(p => {
  const sp = server?.find(x => x.key === p.key);
  return {
    key: p.key, name: sp?.name || p.name, highlight: !!p.highlight,
    price_val: sp?.price_inr ?? p.price, annual_val: sp?.annual_price_inr ?? p.annual ?? 0,
    projects: limitLabel(sp?.projects ?? p.projects, "projects"), users: limitLabel(sp?.users ?? p.users, "users"),
    storage: (sp?.storage_gb ?? p.storage_gb) === -1 ? "Unlimited storage" : `${sp?.storage_gb ?? p.storage_gb} GB storage`,
    features: FEATURES[p.key] || [],
  };
});

const fmtPrice = (n) => `₹${n.toLocaleString("en-IN")}`;

const GST_RATE = 0.18;

const getPlanPricing = (plan, cycle) => {
  if (plan.key === "lifetime") {
    const gst = Math.round(plan.price_val * GST_RATE);
    const payable = plan.price_val + gst;
    return { display: "₹10,00,000", period: " one-time", baseAmount: plan.price_val, gstAmount: gst, payable, savings: null };
  }
  if (cycle === "annual") {
    const annualBase = plan.annual_val || Math.round(plan.price_val * 10);
    const monthlyEffective = Math.round(annualBase / 12);
    const savings = Math.max(0, plan.price_val * 12 - annualBase);
    const gst = Math.round(annualBase * GST_RATE);
    const payable = annualBase + gst;
    return { display: fmtPrice(monthlyEffective), period: "/mo", baseAmount: annualBase, gstAmount: gst, payable, savings, annualLabel: `${fmtPrice(annualBase)}/year` };
  }
  const gst = Math.round(plan.price_val * GST_RATE);
  const payable = plan.price_val + gst;
  return { display: fmtPrice(plan.price_val), period: "/month", baseAmount: plan.price_val, gstAmount: gst, payable, savings: null };
};

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function BillingPage() {
  const { workspace, refreshWorkspace } = useAuth();
  const [loading, setLoading] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [storage, setStorage] = useState(null);
  const [showStorage, setShowStorage] = useState(false);
  const [serverPlans, setServerPlans] = useState(null);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const PLANS = buildPlans(serverPlans);

  useEffect(() => {
    // Pre-load Razorpay checkout script so modal opens immediately on click
    loadRazorpayScript();
    const fetchUsage = async () => {
      try {
        const { data } = await axios.get(`${API}/billing/usage`);
        setUsage(data);
      } catch (err) { console.error(err); }
    };
    fetchUsage();
    axios.get(`${API}/billing/plans`).then(r => setServerPlans(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (showStorage && !storage) axios.get(`${API}/billing/storage`).then(r => setStorage(r.data)).catch(() => {});
  }, [showStorage, storage]);

  const plan = workspace?.subscription_plan || "trial";
  const status = workspace?.subscription_status || "trial";

  const trialEnd = workspace?.trial_end_date
    ? new Date(workspace.trial_end_date)
    : workspace?.created_at
      ? new Date(new Date(workspace.created_at).getTime() + 14 * 86400000)
      : null;
  const daysLeft = trialEnd ? Math.max(0, Math.ceil((trialEnd - new Date()) / 86400000)) : 14;

  const statusConfig = {
    trial:     { color: "text-blue-700",   bg: "bg-blue-50",    border: "border-blue-200",   icon: Clock,         label: "Free Trial" },
    active:    { color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", icon: CheckCircle,   label: "Active" },
    suspended: { color: "text-red-700",     bg: "bg-red-50",     border: "border-red-200",    icon: AlertTriangle, label: "Suspended" },
    expired:   { color: "text-slate-600",   bg: "bg-slate-50",   border: "border-slate-200",  icon: AlertTriangle, label: "Expired" },
  };
  const sc = statusConfig[status] || statusConfig.trial;
  const StatusIcon = sc.icon;

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`${API}/billing/history`);
      setHistory(data);
    } catch (err) { console.error(err); }
    setHistoryLoading(false);
  };

  useEffect(() => {
    if (showHistory && history.length === 0) fetchHistory();
  }, [showHistory]);

  const handleUpgrade = async (planKey) => {
    setLoading(planKey);
    const loadingToast = toast.loading("Opening payment gateway...", { duration: 20000 });
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.dismiss(loadingToast);
        toast.error("Payment gateway failed to load. Check internet connection.");
        setLoading(null);
        return;
      }

      // Create order
      const { data: order } = await axios.post(`${API}/billing/create-order`, { plan: planKey, billing_cycle: billingCycle });

      const selectedPlan = PLANS.find(p => p.key === planKey);
      const pricing = getPlanPricing(selectedPlan, planKey === "lifetime" ? "monthly" : billingCycle);

      toast.dismiss(loadingToast);

      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "SunBiz",
        description: `${order.plan_name} Plan — ${billingCycle === "annual" ? "Annual" : "Monthly"}`,
        order_id: order.order_id,
        prefill: {
          name: workspace?.name || "",
          email: "",
        },
        notes: {
          workspace_id: workspace?.id,
          plan: planKey,
          billing_cycle: billingCycle,
        },
        theme: { color: "#2F7D52" },
        handler: async (response) => {
          try {
            const { data: result } = await axios.post(`${API}/billing/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success(result.message || "Payment successful!");
            if (refreshWorkspace) await refreshWorkspace();
            fetchHistory();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
          }
          setLoading(null);
        },
        modal: {
          ondismiss: () => setLoading(null),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp.error.description}`);
        setLoading(null);
      });
      rzp.open();
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setLoading(null);
    }
  };

  const currentPlanData = PLANS.find(p => p.key === plan);

  return (
    <div className="p-6 max-w-5xl animate-fade-up">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-2xl text-[#0A1128]">Billing & Subscription</h1>
        <button
          onClick={() => setShowHistory(!showHistory)}
          className="flex items-center gap-2 text-xs text-[#64748B] hover:text-[#0A1128] border border-[#E2E8F0] px-3 py-2 rounded transition-colors"
          data-testid="billing-history-toggle"
        >
          <History size={13} />
          {showHistory ? "Hide History" : "Payment History"}
        </button>
      </div>

      {/* Current Plan Status */}
      <div className={`bg-white border rounded-lg p-5 mb-6 ${sc.border}`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] font-semibold text-[#64748B] uppercase tracking-wider mb-1">Current Plan</div>
            <div className="font-heading font-bold text-xl text-[#0A1128]">
              {currentPlanData?.name || "Free Trial"}
            </div>
            {plan !== "trial" && currentPlanData && (
              <div className="text-sm text-[#64748B] mt-0.5">
                {getPlanPricing(currentPlanData, "monthly").display}/month
              </div>
            )}
            {/* Subscription end date */}
            {status === "active" && workspace?.subscription_end_date && (
              <div className="flex items-center gap-1.5 mt-2">
                <Calendar size={11} className="text-emerald-500" />
                <span className="text-xs text-[#64748B]">
                  Renews on{" "}
                  <span className="font-semibold text-[#0A1128]">
                    {new Date(workspace.subscription_end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                </span>
              </div>
            )}
            {status === "expired" && workspace?.subscription_end_date && (
              <div className="flex items-center gap-1.5 mt-2">
                <Calendar size={11} className="text-red-500" />
                <span className="text-xs text-red-600">
                  Expired on{" "}
                  <span className="font-semibold">
                    {new Date(workspace.subscription_end_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                </span>
              </div>
            )}
          </div>
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${sc.bg} ${sc.color}`}>
            <StatusIcon size={12} />
            {sc.label}
          </div>
        </div>

        {status === "trial" && (
          <div className={`mt-4 rounded p-3 flex items-start gap-3 ${daysLeft <= 3 ? "bg-red-50 border border-red-200" : "bg-amber-50 border border-amber-200"}`}>
            <Clock size={14} className={`mt-0.5 flex-shrink-0 ${daysLeft <= 3 ? "text-red-600" : "text-amber-600"}`} />
            <div>
              <div className={`font-semibold text-sm ${daysLeft <= 3 ? "text-red-700" : "text-amber-700"}`}>
                {daysLeft} day{daysLeft !== 1 ? "s" : ""} left in free trial
              </div>
              <p className={`text-xs mt-0.5 ${daysLeft <= 3 ? "text-red-600" : "text-amber-600"}`}>
                Upgrade below to continue using SunBiz without interruption.
              </p>
            </div>
          </div>
        )}

        {status === "trial" && usage && (
          <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(46,100,68,0.1)" }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-3" style={{ color: "#8AA496" }}>Trial Usage</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="flex items-center gap-1.5 text-xs" style={{ color: "#5A7264" }}><FolderOpen size={11} /> Projects</span>
                  <span className="text-xs font-semibold" style={{ color: "#1B2B22" }}>{usage.projects_used} / {usage.projects_limit}</span>
                </div>
                <div style={{ height: "5px", background: "rgba(255,255,255,0.08)", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (usage.projects_used / usage.projects_limit) * 100)}%`, background: usage.projects_used >= usage.projects_limit ? "#EF4444" : "linear-gradient(90deg, #3B82F6, #60A5FA)", borderRadius: "4px", transition: "width 0.8s ease" }} />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="flex items-center gap-1.5 text-xs" style={{ color: "#5A7264" }}><Users size={11} /> Members</span>
                  <span className="text-xs font-semibold" style={{ color: "#1B2B22" }}>{usage.users_used} / {usage.users_limit}</span>
                </div>
                <div style={{ height: "5px", background: "rgba(255,255,255,0.08)", borderRadius: "4px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (usage.users_used / usage.users_limit) * 100)}%`, background: usage.users_used >= usage.users_limit ? "#EF4444" : "linear-gradient(90deg, #8B5CF6, #A78BFA)", borderRadius: "4px", transition: "width 0.8s ease" }} />
                </div>
              </div>
            </div>
          </div>
        )}

        {status === "suspended" && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded p-3 flex items-start gap-3">
            <AlertTriangle size={14} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-semibold text-sm text-red-700">Account Suspended</div>
              <p className="text-xs text-red-600 mt-0.5">Contact support at billing@sunbizcon.com to restore access.</p>
            </div>
          </div>
        )}

        {usage && (
          <div className="mt-4 pt-4" style={{ borderTop: "1px solid rgba(46,100,68,0.1)" }}>
            <div className="text-[10px] font-semibold uppercase tracking-wider mb-3" style={{ color: "#8AA496" }}>Plan Usage{status !== "active" ? ` — ${usage.plan_name}` : ""}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {/* Projects meter */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="flex items-center gap-1.5 text-xs" style={{ color: "#5A7264" }}>
                    <FolderOpen size={11} /> Projects
                  </span>
                  <span className="text-xs font-semibold" style={{ color: "#1B2B22" }}>
                    {usage ? `${usage.projects_used} / ${usage.projects_limit === -1 ? "∞" : usage.projects_limit}` : "—"}
                  </span>
                </div>
                <div style={{ height: "5px", background: "rgba(255,255,255,0.08)", borderRadius: "4px", overflow: "hidden" }}>
                  {usage && usage.projects_limit !== -1 && (
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.min(100, (usage.projects_used / usage.projects_limit) * 100)}%`,
                        background: usage.projects_used >= usage.projects_limit ? "#EF4444" : "linear-gradient(90deg, #3B82F6, #60A5FA)",
                        borderRadius: "4px",
                        transition: "width 0.8s ease",
                      }}
                    />
                  )}
                  {usage?.projects_limit === -1 && (
                    <div style={{ height: "100%", width: "100%", background: "linear-gradient(90deg, #3B82F6, #22D3EE)", borderRadius: "4px" }} />
                  )}
                </div>
              </div>
              {/* Members meter */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="flex items-center gap-1.5 text-xs" style={{ color: "#5A7264" }}>
                    <Users size={11} /> Members
                  </span>
                  <span className="text-xs font-semibold" style={{ color: "#1B2B22" }}>
                    {usage ? `${usage.users_used} / ${usage.users_limit === -1 ? "∞" : usage.users_limit}` : "—"}
                  </span>
                </div>
                <div style={{ height: "5px", background: "rgba(255,255,255,0.08)", borderRadius: "4px", overflow: "hidden" }}>
                  {usage && usage.users_limit !== -1 && (
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.min(100, (usage.users_used / usage.users_limit) * 100)}%`,
                        background: usage.users_used >= usage.users_limit ? "#EF4444" : "linear-gradient(90deg, #8B5CF6, #A78BFA)",
                        borderRadius: "4px",
                        transition: "width 0.8s ease",
                      }}
                    />
                  )}
                  {usage?.users_limit === -1 && (
                    <div style={{ height: "100%", width: "100%", background: "linear-gradient(90deg, #8B5CF6, #22D3EE)", borderRadius: "4px" }} />
                  )}
                </div>
              </div>
              {/* Storage meter */}
              <div data-testid="storage-meter">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="flex items-center gap-1.5 text-xs" style={{ color: "#5A7264" }}>
                    <HardDrive size={11} /> File storage
                  </span>
                  <span className="text-xs font-semibold" style={{ color: "#1B2B22" }}>
                    {usage ? `${fmtBytes(usage.storage_used_bytes)} / ${usage.storage_limit_bytes === -1 ? "∞" : `${usage.storage_limit_gb} GB`}` : "—"}
                  </span>
                </div>
                <div style={{ height: "5px", background: "rgba(255,255,255,0.08)", borderRadius: "4px", overflow: "hidden" }}>
                  {usage && usage.storage_limit_bytes !== -1 && (
                    <div style={{ height: "100%", width: `${Math.min(100, (usage.storage_used_bytes / usage.storage_limit_bytes) * 100)}%`,
                      background: usage.storage_used_bytes >= usage.storage_limit_bytes * 0.9 ? "#EF4444" : "linear-gradient(90deg, #2F7D52, #34D399)", borderRadius: "4px", transition: "width 0.8s ease" }} />
                  )}
                  {usage?.storage_limit_bytes === -1 && <div style={{ height: "100%", width: "100%", background: "linear-gradient(90deg, #2F7D52, #22D3EE)", borderRadius: "4px" }} />}
                </div>
                <button onClick={() => setShowStorage(v => !v)} className="text-[10px] mt-1 hover:underline" style={{ color: "#2F7D52" }} data-testid="storage-breakdown-toggle">{showStorage ? "Hide breakdown" : "Which projects use the most?"}</button>
              </div>
            </div>
            {showStorage && (
              <div className="mt-3 rounded-lg border border-[#E2E8F0] overflow-hidden" data-testid="storage-breakdown">
                <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#64748B] bg-[#F8FAFC] flex flex-wrap gap-3">
                  {usage?.storage_breakdown && Object.entries(usage.storage_breakdown).filter(([, v]) => v > 0).map(([k, v]) => <span key={k}>{k.replace(/_/g, " ")}: <b className="text-[#1B2B22]">{fmtBytes(v)}</b></span>)}
                  {usage?.storage_breakdown && !Object.values(usage.storage_breakdown).some(v => v > 0) && <span>No files uploaded yet.</span>}
                </div>
                {!storage && <div className="px-3 py-3 text-xs text-[#94A3B8]">Loading…</div>}
                {storage && storage.projects?.slice(0, 10).map(p => (
                  <div key={p.project_id} className="flex items-center gap-3 px-3 py-1.5 border-t border-[#F1F5F9] text-xs">
                    <span className="flex-1 truncate text-[#1B2B22]">{p.project_name}{p.is_archived ? <span className="ml-1 text-[#94A3B8]">(archived)</span> : ""}</span>
                    <span className="text-[#64748B]">{p.documents ? `docs ${fmtBytes(p.documents)}` : ""}{p.dpr_annexures ? ` · DPR ${fmtBytes(p.dpr_annexures)}` : ""}</span>
                    <span className="font-semibold tabular-nums text-[#1B2B22] w-16 text-right">{fmtBytes(p.bytes)}</span>
                  </div>
                ))}
                {storage && storage.projects?.length === 0 && <div className="px-3 py-3 text-xs text-[#94A3B8]">No project files yet.</div>}
              </div>
            )}
            {usage?.days_remaining !== null && usage?.days_remaining !== undefined && (
              <div className="mt-3 text-xs" style={{ color: "#8AA496" }}>
                Renews in <span style={{ color: "#5A7264", fontWeight: "600" }}>{usage.days_remaining} day{usage.days_remaining !== 1 ? "s" : ""}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Payment History */}
      {showHistory && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg mb-6 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0]">
            <h3 className="font-heading font-semibold text-sm text-[#0A1128] flex items-center gap-2">
              <History size={14} />Payment History
            </h3>
            <button onClick={fetchHistory} className="p-1 rounded hover:bg-slate-50">
              <RefreshCw size={12} className={historyLoading ? "animate-spin text-[#64748B]" : "text-[#94A3B8]"} />
            </button>
          </div>
          {history.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-[#64748B]" data-testid="no-billing-history">
              No payment history yet
            </div>
          ) : (
            <div className="divide-y divide-[#F1F5F9]">
              {history.map((h) => (
                <div key={h.order_id} className="px-5 py-3 flex items-center justify-between" data-testid={`billing-history-${h.order_id}`}>
                  <div>
                    <div className="text-sm font-medium text-[#0A1128] capitalize">{h.plan} Plan</div>
                    <div className="text-xs text-[#94A3B8]">{new Date(h.created_at).toLocaleDateString("en-IN")} · {h.order_id}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-sm font-semibold text-[#0A1128]">₹{(h.amount_paise / 100).toLocaleString("en-IN")}</div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${h.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {h.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Plan Cards */}
      <div>
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-heading font-bold text-base text-[#0A1128]">
            {status === "active" ? "Upgrade or Change Plan" : "Choose a Plan"}
          </h2>
          {/* Monthly / Annual toggle */}
          <div className="flex items-center gap-1 bg-[#F1F5F9] p-1 rounded-lg">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${billingCycle === "monthly" ? "bg-white text-[#0A1128] shadow-sm" : "text-[#64748B] hover:text-[#0A1128]"}`}
              data-testid="billing-cycle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("annual")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-all ${billingCycle === "annual" ? "bg-white text-[#0A1128] shadow-sm" : "text-[#64748B] hover:text-[#0A1128]"}`}
              data-testid="billing-cycle-annual"
            >
              Annual
              <span className="bg-emerald-100 text-emerald-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full">2 MONTHS FREE</span>
            </button>
          </div>
        </div>

        {/* GST Notice */}
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 mb-4 text-xs text-amber-800">
          <Zap size={12} className="text-amber-600 flex-shrink-0" />
          All prices are <strong className="mx-0.5">exclusive of GST</strong>. 18% GST is applicable on all plans. Final invoice will include GST as per prevailing rates.
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PLANS.map((p) => {
            const isCurrent = p.key === plan && status === "active";
            const isLoading = loading === p.key;
            const pricing = getPlanPricing(p, billingCycle);
            return (
              <div
                key={p.key}
                className={`relative rounded-lg border p-5 transition-all ${isCurrent ? "border-blue-500/40 bg-blue-500/08" : "border-[#E2E8F0] bg-white hover:border-[rgba(47,125,82,0.3)] hover:shadow-sm"} ${p.highlight && !isCurrent ? "ring-1 ring-blue-500/20" : ""}`}
                data-testid={`plan-card-${p.key}`}
              >
                {p.highlight && !isCurrent && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#2F7D52] text-white text-[9px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full whitespace-nowrap">
                    Most Popular
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-emerald-500 text-[#1B2B22] text-[9px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full whitespace-nowrap">
                    Current Plan
                  </div>
                )}

                <div className="font-heading font-bold text-sm text-[#0A1128] mb-1">{p.name}</div>
                <div className="font-heading font-black text-xl text-[#0A1128]">
                  {pricing.display}
                  <span className="text-xs font-normal text-[#64748B]">{pricing.period}</span>
                </div>
                {/* GST breakdown */}
                <div className="mt-1 mb-1">
                  {billingCycle === "annual" && p.key !== "lifetime" ? (
                    <div className="space-y-0.5">
                      <div className="text-[10px] text-[#94A3B8]">{pricing.annualLabel} + {fmtPrice(pricing.gstAmount)} GST</div>
                      <div className="text-xs font-bold text-emerald-600">{fmtPrice(pricing.payable)}/year incl. GST</div>
                      {pricing.savings && <div className="text-[10px] text-emerald-500">Save {fmtPrice(pricing.savings)} vs monthly</div>}
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      <div className="text-[10px] text-[#94A3B8]">+ {fmtPrice(pricing.gstAmount)} GST (18%)</div>
                      <div className="text-xs font-bold text-[#0A1128]">{fmtPrice(pricing.payable)} payable</div>
                    </div>
                  )}
                </div>

                <div className="mt-3 text-[11px] text-[#1B2B22] font-semibold">{p.projects} · {p.users}</div>
                <div className="text-[10px] text-[#64748B] mb-1">{p.storage}</div>
                <div className="my-3 space-y-1.5">
                  {p.features.map((f) => (
                    <div key={f} className="flex items-start gap-1.5 text-xs text-[#64748B]">
                      <CheckCircle size={10} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      {f}
                    </div>
                  ))}
                </div>

                {isCurrent ? (
                  <div className="text-center text-xs font-semibold text-[#2F7D52] py-2" data-testid={`plan-current-${p.key}`}>
                    Active Plan
                  </div>
                ) : p.key === "lifetime" ? (
                  <a
                    href={`mailto:${SALES_EMAIL}?subject=${encodeURIComponent("SunBiz Lifetime plan enquiry")}&body=${encodeURIComponent(`Hi Akshay,\n\nWe'd like to know more about the Lifetime plan for ${workspace?.name || "our company"}.\n`)}`}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded text-xs font-semibold bg-[#F5F8F5] text-[#1B2B22] hover:bg-[#E6F4EC] transition-colors"
                    data-testid="upgrade-btn-lifetime"
                  >
                    Contact Sales
                  </a>
                ) : (
                  <button
                    onClick={() => handleUpgrade(p.key)}
                    disabled={!!loading}
                    className={`w-full flex items-center justify-center gap-2 py-2.5 rounded text-xs font-semibold transition-colors ${p.highlight ? "bg-[#2F7D52] text-white hover:bg-[#266B44]" : "bg-[#F5F8F5] text-[#1B2B22] hover:bg-[#E6F4EC]"} disabled:opacity-50 disabled:cursor-not-allowed`}
                    data-testid={`upgrade-btn-${p.key}`}
                  >
                    {isLoading ? (
                      <RefreshCw size={13} className="animate-spin" />
                    ) : (
                      <><CreditCard size={13} />Pay {fmtPrice(pricing.payable)} (incl. GST)</>
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-[#94A3B8] mt-4 text-center">
          Secure payment via Razorpay · Supports UPI, Net Banking, Cards &amp; Wallets · All prices exclusive of 18% GST · GST invoice provided
        </p>
      </div>
    </div>
  );
}
